/** Reserved domain so workstation usernames can use Firebase email/password without a mailbox. */
export const WORKSTATION_AUTH_DOMAIN = 'workstation.lctf.invalid';

/** Reserved domain for QA logins created by `scripts/create-crm-test-users.cjs`. */
export const TEST_AUTH_DOMAIN = 'lctf-cutover.invalid';

/** Short usernames for the existing CRM test accounts. */
export const TEST_USERNAME_ALIASES: Record<string, string> = {
  'test-staff': 'crm-test-staff',
  'test-1099': 'crm-test-contractor-1099',
  'test-employee': 'crm-test-employee',
  'test-employee-office': 'crm-test-employee-office',
  'test-sales-rep': 'crm-test-sales-rep',
  'test-jacob': 'crm-test-jacob-temple',
  'test-exec': 'crm-test-executive',
  'test-architect': 'crm-test-architect',
  'test-engineer': 'crm-test-engineer',
  'test-cpa': 'crm-test-cpa',
  'test-supplier': 'crm-test-supplier',
  'test-client': 'crm-test-client',
};

export function normalizeWorkstationUsername(raw: string): string | null {
  const slug = raw
    .trim()
    .toLowerCase()
    .replace(/[\s_]+/g, '-')
    .replace(/[^a-z0-9-]/g, '')
    .replace(/-+/g, '-')
    .replace(/^-|-$/g, '');
  if (slug.length < 2 || slug.length > 32) return null;
  if (!/^[a-z][a-z0-9-]*$/.test(slug)) return null;
  return slug;
}

export function workstationAuthEmail(username: string): string {
  return `${username}@${WORKSTATION_AUTH_DOMAIN}`;
}

export function isWorkstationAuthEmail(email: string): boolean {
  return email.trim().toLowerCase().endsWith(`@${WORKSTATION_AUTH_DOMAIN}`);
}

export function isTestAuthEmail(email: string): boolean {
  return email.trim().toLowerCase().endsWith(`@${TEST_AUTH_DOMAIN}`);
}

export function isReservedAuthEmail(email: string): boolean {
  return isWorkstationAuthEmail(email) || isTestAuthEmail(email);
}

export function workstationUsernameFromEmail(email: string): string | null {
  const trimmed = email.trim().toLowerCase();
  if (!isWorkstationAuthEmail(trimmed)) return null;
  return trimmed.slice(0, -(WORKSTATION_AUTH_DOMAIN.length + 1));
}

export function testUsernameFromEmail(email: string): string | null {
  const trimmed = email.trim().toLowerCase();
  if (!isTestAuthEmail(trimmed)) return null;
  const local = trimmed.slice(0, -(TEST_AUTH_DOMAIN.length + 1));
  const alias = Object.entries(TEST_USERNAME_ALIASES).find(([, canonical]) => canonical === local);
  return alias?.[0] || local;
}

export function formatLoginIdentifier(email: string): string {
  return workstationUsernameFromEmail(email) || testUsernameFromEmail(email) || email;
}

export function resolveLoginIdentifier(raw: string): string {
  const trimmed = raw.trim();
  if (!trimmed) {
    throw new Error('Enter your email or username');
  }
  if (trimmed.includes('@')) {
    return trimmed.toLowerCase();
  }
  const username = normalizeWorkstationUsername(trimmed);
  if (!username) {
    throw new Error('Usernames use letters, numbers, and hyphens (e.g. job-trailer).');
  }
  const canonical = TEST_USERNAME_ALIASES[username] || username;
  if (canonical.startsWith('crm-test-')) {
    return `${canonical}@${TEST_AUTH_DOMAIN}`;
  }
  return workstationAuthEmail(username);
}

export function resolveInviteAuthEmail(raw: string): { ok: true; email: string; loginLabel: string } | { ok: false; error: string } {
  const trimmed = raw.trim();
  if (!trimmed) {
    return { ok: false, error: 'Enter a username or email' };
  }
  if (trimmed.includes('@')) {
    const email = trimmed.toLowerCase();
    return { ok: true, email, loginLabel: formatLoginIdentifier(email) };
  }
  const username = normalizeWorkstationUsername(trimmed);
  if (!username) {
    return { ok: false, error: 'Usernames use letters, numbers, and hyphens (e.g. job-trailer).' };
  }
  return { ok: true, email: workstationAuthEmail(username), loginLabel: username };
}
